import { useEffect, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Info } from 'lucide-react';
import { api, fmt } from '../api';
import { CATEGORY_KO } from '../labels';
import MerchantDetailModal from '../components/MerchantDetailModal';
import { SkeletonTable } from '../components/Skeleton';

type Row = {
  mid: string;
  name: string;
  category: string;
  ceo_name: string | null;
  business_no: string | null;
  last_trx_date: string | null;
  dormant_days: number | null;
  latest_settle_date: string | null;
  cumulative_repaid: number;
  prev_shortfall: number;
  principal_repaid_today: number;
  // 20260908: 목록 최근 원금반환 컬럼 기준 (가맹점 상세와 동일).
  // WHERE principal_repaid > 0 → MAX(settle_date) 1건. 회수 이력 없으면 둘 다 null.
  last_repay_amount: number | null;
  last_repay_date: string | null;
  remaining_amount: number;
  purchase_amount: number;
  litigation_status?: 'none' | 'in_progress' | 'ended' | null;
  litigation_type?: string | null;
  litigation_last_action_date?: string | null;
  litigation_has_cost?: number;
  litigation_cumulative_cost?: number;
  contract_ended_at?: string | null;
};

type LitFilter = 'all' | 'in_progress' | 'has_cost' | 'contract_ended';
const LITIGATION_TYPE_KO: Record<string, string> = {
  payment_order: '지급명령',
  civil_suit: '민사소송',
  other: '기타',
};
type SortKey = 'mid' | 'name' | 'category' | 'last_trx_date' | 'dormant_days' | 'remaining_amount';
type SortDir = 'asc' | 'desc';

function compareValues(a: unknown, b: unknown, dir: SortDir): number {
  const an = a == null;
  const bn = b == null;
  if (an && bn) return 0;
  if (an) return dir === 'asc' ? 1 : -1;
  if (bn) return dir === 'asc' ? -1 : 1;
  if (typeof a === 'number' && typeof b === 'number') return dir === 'asc' ? a - b : b - a;
  const sa = String(a), sb = String(b);
  return dir === 'asc' ? sa.localeCompare(sb, 'ko') : sb.localeCompare(sa, 'ko');
}

function SortHeader({
  label, sortKey, current, dir, onClick, className, title,
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey | null;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  className?: string;
  title?: string;
}) {
  const active = current === sortKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  const cls = ['sortable', className].filter(Boolean).join(' ');
  return (
    <th className={cls} title={title} onClick={() => onClick(sortKey)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        <Icon size={12} style={{ opacity: active ? 1 : 0.45, color: active ? 'var(--brand-700)' : 'var(--text-muted)' }} />
      </span>
    </th>
  );
}

function todayKstStr(): string {
  const kst = new Date(Date.now() + 9 * 3600 * 1000);
  return kst.toISOString().slice(0, 10);
}

function shiftDaysStr(base: string, delta: number): string {
  const d = new Date(base + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

type DormantFilter = 'all' | 'no_history' | '3plus' | '7plus' | '30plus';
type CatFilter = 'all' | 'repay' | 'normal' | 'bad';

const LIT_FILTER_LABEL: Record<LitFilter, string> = {
  all: '전체',
  in_progress: '소송 진행 중',
  has_cost: '소송비용 발생',
  contract_ended: '계약 종료',
};

export default function Dormant() {
  const [data, setData] = useState<Row[]>([]);
  const [asof, setAsof] = useState<string>(todayKstStr());
  const [days, setDays] = useState<number>(3);
  const [loading, setLoading] = useState(true);
  const [detailMid, setDetailMid] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [durFilter, setDurFilter] = useState<DormantFilter>('all');
  const [catFilter, setCatFilter] = useState<CatFilter>('all');
  const [litFilter, setLitFilter] = useState<LitFilter>('all');

  const [sortKey, setSortKey] = useState<SortKey>('dormant_days');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('desc'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api<{ data: Row[]; asof: string; days: number }>(
        'GET', `/settlements/alerts/dormant?asof=${asof}&days=${days}`
      );
      setData(r.data ?? []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asof, days]);

  const rangeStart = shiftDaysStr(asof, -(days - 1));

  const filtered = useMemo(() => {
    let arr = data;
    if (catFilter !== 'all') arr = arr.filter(r => r.category === catFilter);
    if (durFilter === 'no_history') arr = arr.filter(r => r.dormant_days == null);
    else if (durFilter === '3plus')  arr = arr.filter(r => (r.dormant_days ?? Infinity) >= 3);
    else if (durFilter === '7plus')  arr = arr.filter(r => (r.dormant_days ?? Infinity) >= 7);
    else if (durFilter === '30plus') arr = arr.filter(r => (r.dormant_days ?? Infinity) >= 30);
    if (litFilter === 'in_progress')   arr = arr.filter(r => r.litigation_status === 'in_progress');
    else if (litFilter === 'has_cost') arr = arr.filter(r => (r.litigation_has_cost ?? 0) === 1);
    else if (litFilter === 'contract_ended') arr = arr.filter(r => !!r.contract_ended_at);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const qDigits = s.replace(/\D/g, '');
      arr = arr.filter(r => {
        const bnoDigits = (r.business_no ?? '').replace(/\D/g, '');
        return r.mid.toLowerCase().includes(s)
            || r.name.toLowerCase().includes(s)
            || (r.ceo_name?.toLowerCase().includes(s) ?? false)
            || (r.business_no?.toLowerCase().includes(s) ?? false)
            || (qDigits.length >= 3 && bnoDigits.includes(qDigits));
      });
    }
    const sorted = [...arr];
    sorted.sort((a, b) => compareValues(a[sortKey] as any, b[sortKey] as any, sortDir));
    return sorted;
  }, [data, catFilter, durFilter, litFilter, q, sortKey, sortDir]);

  const litInProgressCount = data.filter(r => r.litigation_status === 'in_progress').length;
  const litHasCostCount = data.filter(r => (r.litigation_has_cost ?? 0) === 1).length;
  const contractEndedCount = data.filter(r => !!r.contract_ended_at).length;

  const cnt = (fn: (r: Row) => boolean) => data.filter(fn).length;
  const noHistCount = cnt(r => r.dormant_days == null);
  const c3 = cnt(r => (r.dormant_days ?? Infinity) >= 3);
  const c7 = cnt(r => (r.dormant_days ?? Infinity) >= 7);
  const c30 = cnt(r => (r.dormant_days ?? Infinity) >= 30);

  return (
    <div>
      <h2>무매출 알림</h2>

      <div className="card">
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <label>기준일</label>
            <input type="date" value={asof} onChange={e => setAsof(e.target.value)} />
          </div>
          <div>
            <label>기준 기간 (일)</label>
            <select value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={3}>3일 (기본)</option>
              <option value={7}>7일</option>
              <option value={14}>14일</option>
              <option value={30}>30일</option>
              <option value={60}>60일</option>
              <option value={90}>90일</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label>검색 (가맹점명 · 대표자명 · MID · 사업자등록번호)</label>
            <input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="예: 김성숙 · 파라앤홈 · gpay7476dm · 123-45-67890"
                   style={{ width: '100%' }} />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button className="btn ghost" onClick={load}>새로고침</button>
          </div>
        </div>

        <div className="callout" style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Info size={16} style={{ color: 'var(--brand-700)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
            <b>무매출 기준</b>: 기준일 <b>{asof}</b>에서 최근 <b>{days}일</b>({rangeStart} ~ {asof}) 동안 매출이 <b>0건</b>인 활성 가맹점 목록.
            <br />
            <b>무매출 경과일</b> = 기준일 − 마지막 매출일. 마지막 매출이 아예 없으면 "매출 이력 없음"으로 별도 표시.
          </div>
        </div>

        {/* 무매출 기간 필터 (칩) */}
        <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {([
            ['all',     `전체 (${data.length})`],
            ['3plus',   `3일 이상 (${c3})`],
            ['7plus',   `7일 이상 (${c7})`],
            ['30plus',  `30일 이상 (${c30})`],
            ['no_history', `매출 이력 없음 (${noHistCount})`],
          ] as const).map(([v, label]) => (
            <button key={v}
                    onClick={() => setDurFilter(v)}
                    className={durFilter === v ? 'filter-primary' : 'filter-ghost'}
                    style={{ fontSize: 12, padding: '4px 10px' }}>
              {label}
            </button>
          ))}
          <span style={{ marginLeft: 8, color: 'var(--muted)', fontSize: 12, alignSelf: 'center' }}>고객분류</span>
          {([
            ['all',    '전체'],
            ['repay',  '반환'],
            ['normal', '일반'],
            ['bad',    '악성'],
          ] as const).map(([v, label]) => (
            <button key={v}
                    onClick={() => setCatFilter(v)}
                    className={catFilter === v ? 'filter-primary' : 'filter-ghost'}
                    style={{ fontSize: 12, padding: '4px 10px' }}>
              {label}
            </button>
          ))}
          <span style={{ marginLeft: 8, color: 'var(--muted)', fontSize: 12, alignSelf: 'center' }}>소송·계약</span>
          {([
            ['all',           LIT_FILTER_LABEL.all],
            ['in_progress',   `${LIT_FILTER_LABEL.in_progress} (${litInProgressCount})`],
            ['has_cost',      `${LIT_FILTER_LABEL.has_cost} (${litHasCostCount})`],
            ['contract_ended',`${LIT_FILTER_LABEL.contract_ended} (${contractEndedCount})`],
          ] as const).map(([v, label]) => (
            <button key={v}
                    onClick={() => setLitFilter(v)}
                    className={litFilter === v ? 'filter-primary' : 'filter-ghost'}
                    style={{ fontSize: 12, padding: '4px 10px' }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? <SkeletonTable rows={5} cols={6} showHeader /> : (
          <>
            <p style={{ marginTop: 14, color: filtered.length > 0 ? 'var(--warn)' : 'var(--good)' }}>
              {filtered.length === 0
                ? '조회 조건에 해당하는 무매출 가맹점이 없습니다.'
                : `${filtered.length.toLocaleString('ko-KR')}개 가맹점이 무매출 상태입니다.`}
            </p>
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <SortHeader label="MID"          sortKey="mid"              current={sortKey} dir={sortDir} onClick={onSort} className="nowrap" />
                  <SortHeader label="가맹점명"      sortKey="name"             current={sortKey} dir={sortDir} onClick={onSort} />
                  {/* 대표자명 — 실제 화면 폭 ~90px 목표. 좌우 padding만 6px로 축소, 세로 padding·폰트·정렬 유지. */}
                  <th className="nowrap" style={{ width: 90, maxWidth: 90, paddingLeft: 6, paddingRight: 6 }}>대표자명</th>
                  <SortHeader label="고객분류"      sortKey="category"         current={sortKey} dir={sortDir} onClick={onSort} className="nowrap" />
                  <SortHeader label="마지막 매출일" sortKey="last_trx_date"    current={sortKey} dir={sortDir} onClick={onSort} className="nowrap" />
                  <SortHeader label="무매출 경과일" sortKey="dormant_days"     current={sortKey} dir={sortDir} onClick={onSort} className="num nowrap"
                              title="기준일 − 마지막 매출일. 매출 이력이 없으면 '매출 이력 없음' 표시." />
                  <SortHeader label="당일 잔여액"    sortKey="remaining_amount" current={sortKey} dir={sortDir} onClick={onSort} className="num nowrap"
                              title="매입액 − 누적 회수액. 최근 정산 스냅샷 기준." />
                  <th className="num nowrap" title="실제 원금 반환이 발생한 가장 최근 이력의 금액 (가맹점 상세 팝업과 동일 기준). 반환 이력이 없으면 「없음」.">최근 원금반환</th>
                  <th className="nowrap" style={{ width: 108, maxWidth: 108 }}
                      title="가장 최근 원금 반환 발생일 (YYYY-MM-DD). 최근 원금반환과 동일 이력 기준. 반환 이력이 없으면 「-」.">최근 반환일</th>
                  <th className="num nowrap" title="전일까지 미차감 누적 (최근 스냅샷 기준)">전일미차감</th>
                  <th className="nowrap" title="가맹점 수정 화면에서 등록한 소송 진행 상태 · 계약 종료 여부">소송·계약</th>
                </tr></thead>
                <tbody>
                  {filtered.map(r => {
                    const noHist = r.dormant_days == null;
                    const dDaysStyle: React.CSSProperties =
                      noHist ? { color: 'var(--muted)', fontStyle: 'italic' } :
                      (r.dormant_days! >= 30) ? { color: '#7f1d1d', fontWeight: 800, background: '#fee2e2' } :
                      (r.dormant_days! >= 7)  ? { color: '#b91c1c', fontWeight: 700, background: '#fef3c7' } :
                      (r.dormant_days! >= 3)  ? { color: '#854d0e', fontWeight: 600 } :
                      { color: 'var(--text-muted)' };
                    return (
                      <tr key={r.mid}>
                        <td className="nowrap" style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{r.mid}</td>
                        <td>
                          <a onClick={(e) => { e.preventDefault(); setDetailMid(r.mid); }}
                             style={{ fontWeight: 600, cursor: 'pointer' }}>
                            {r.name}
                          </a>
                        </td>
                        {/* 대표자명 — 90px 강제 축소 + 잘림 시 title로 전체값 확인. 좌우 padding만 헤더와 동일하게 6px. */}
                        <td className="nowrap" style={{ paddingLeft: 6, paddingRight: 6, color: r.ceo_name ? undefined : 'var(--muted)' }}
                            title={r.ceo_name ?? undefined}>
                          <span style={{ display: 'inline-block', maxWidth: 78, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                            {r.ceo_name ?? '-'}
                          </span>
                        </td>
                        <td className="nowrap">
                          <span className={`chip cat-${r.category}`}>
                            <span className="dot" />{CATEGORY_KO[r.category] ?? r.category}
                          </span>
                        </td>
                        <td className="nowrap">{r.last_trx_date ? fmt.date(r.last_trx_date) : <span style={{ color: 'var(--muted)' }}>매출 이력 없음</span>}</td>
                        <td className="num nowrap" style={dDaysStyle}>
                          {noHist ? '매출 이력 없음' : `무매출 ${r.dormant_days}일`}
                        </td>
                        <td className="num nowrap" style={{ color: r.remaining_amount > 0 ? '#c2410c' : 'var(--leaf-700)' }}>
                          {fmt.won(r.remaining_amount)}
                        </td>
                        <td className="num nowrap"
                            title={r.last_repay_date ? `최근 반환일 ${r.last_repay_date}` : '반환 이력 없음'}>
                          {r.last_repay_date == null
                            ? <span style={{ color: 'var(--text-muted)' }}>없음</span>
                            : fmt.won(r.last_repay_amount ?? 0)}
                        </td>
                        {/* 최근 반환일 — 최근 원금반환과 동일 이력의 settle_date. */}
                        <td className="nowrap" style={{ fontFamily: 'monospace', color: r.last_repay_date ? undefined : 'var(--text-muted)' }}>
                          {r.last_repay_date ?? '-'}
                        </td>
                        <td className="num nowrap">{fmt.won(r.prev_shortfall)}</td>
                        <td className="nowrap">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
                            {r.litigation_status === 'in_progress' && (
                              <span style={{ padding: '1px 6px', borderRadius: 4, background: '#fee2e2', color: '#991b1b', fontWeight: 700, alignSelf: 'flex-start' }}
                                    title={[
                                      r.litigation_type ? LITIGATION_TYPE_KO[r.litigation_type] ?? r.litigation_type : null,
                                      r.litigation_last_action_date ? `최근 진행 ${r.litigation_last_action_date}` : null,
                                    ].filter(Boolean).join(' · ')}>
                                소송 진행 중
                              </span>
                            )}
                            {r.litigation_status === 'ended' && (
                              <span style={{ padding: '1px 6px', borderRadius: 4, background: '#f1f5f9', color: '#334155', fontWeight: 600, alignSelf: 'flex-start' }}>
                                소송 종료
                              </span>
                            )}
                            {(r.litigation_has_cost ?? 0) === 1 && (
                              <span style={{ padding: '1px 6px', borderRadius: 4, background: '#fef3c7', color: '#854d0e', fontWeight: 600, alignSelf: 'flex-start' }}
                                    title={(r.litigation_cumulative_cost ?? 0) > 0 ? `누적 ${fmt.won(r.litigation_cumulative_cost ?? 0)}원` : undefined}>
                                비용 발생 {(r.litigation_cumulative_cost ?? 0) > 0 && `(${fmt.won(r.litigation_cumulative_cost ?? 0)}원)`}
                              </span>
                            )}
                            {r.contract_ended_at && (
                              <span style={{ padding: '1px 6px', borderRadius: 4, background: '#e0e7ff', color: '#3730a3', fontWeight: 600, alignSelf: 'flex-start' }}
                                    title={`계약 종료 처리 ${r.contract_ended_at}`}>
                                계약 종료
                              </span>
                            )}
                            {!r.litigation_status || (r.litigation_status === 'none' && !(r.litigation_has_cost ?? 0) && !r.contract_ended_at) ? (
                              <span style={{ color: 'var(--muted)' }}>-</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <MerchantDetailModal mid={detailMid} open={!!detailMid} onClose={() => setDetailMid(null)} />
    </div>
  );
}
